import React from 'react'
import styled from 'styled-components'
import LinkOffIcon from '@mui/icons-material/LinkOff'

import Card from 'pages/HehmDevice/HehmDashboard/Card'
import NoDataValue from 'pages/HehmDevice/HehmDashboard/NoDataValue'
import SysStatusIcon from 'pages/HehmDevice/HehmDashboard/SysStatusIcon'

import { capitalize } from 'helpers/formatters'

import { color } from 'styles/colors'

import HehmDevice from 'types/HehmDevice'

type Props = {
  hehmDevice: HehmDevice | null
  onNavigate: (tab: string) => void
}

const HEALTH_TEXT = {
  green: 'Overall system health is good.',
  yellow: 'System needs maintenance.',
  red: 'Overall system needs attention.'
}

const SystemConditionCard = ({ hehmDevice, onNavigate }: Props) => {
  const filterHealthPercentage = hehmDevice?.health?.filter?.lifeRemaining ? hehmDevice?.health?.filter?.lifeRemaining + '%' : null
  const healthStatus = hehmDevice?.health?.category?.toLowerCase()
  const filterHealthStatus = capitalize(hehmDevice?.health?.filter?.category?.replace(/_/g, ' '))

  const isConnected = hehmDevice?.connectionStatus?.connected
  const disconnectedDuration = Date.now() - new Date(hehmDevice?.connectionStatus?.lastConnectedAt || '').getTime()
  const isRecentlyDisconnected = !isConnected && disconnectedDuration < 24 * 60 * 60 * 1000
  const isDisconnectedForLessThanOneWeek = !isConnected && disconnectedDuration <= 7 * 24 * 60 * 60 * 1000

  const getConnectionStatusText = () => {
    if (isConnected) return 'Connected'
    if (isRecentlyDisconnected) return 'Disconnected < 24 hours'
    if (isDisconnectedForLessThanOneWeek) return 'Disconnected for at least 24 hours'
    return 'Disconnected for at least 1 week'
  }
  const connectionStatusText = getConnectionStatusText()

  const mode = hehmDevice?.health?.current.hvacMode
  const modeIsIdle = mode?.toUpperCase() === 'IDLE'

  const getSystemStatus = () => {
    if (mode?.match(/heating/gi)) return 'Heating'
    if (mode?.match(/cooling/gi)) return 'Cooling'
    return capitalize(mode)
  }

  return (
    <Card
      title="System Condition"
      icon="/img/heart_with_pulse.svg"
      onClick={() => onNavigate('system-condition')}
      iconStyle={{ color: color('ancillaryText') }}
    >
      <Card.ItemLabel>System Health</Card.ItemLabel>
      <Card.ItemValue>
        <S.SystemHealthIcon status={healthStatus || ''} />
        {HEALTH_TEXT[healthStatus as keyof typeof HEALTH_TEXT] || 'unknown'}
      </Card.ItemValue>

      <Card.ItemLabel>Filter Health</Card.ItemLabel>
      <S.FilterHealthValue>
        <S.FilterIcon />
        {filterHealthPercentage} {filterHealthStatus || <NoDataValue />}
      </S.FilterHealthValue>

      <Card.ItemLabel>Communication Status</Card.ItemLabel>
      <Card.ItemValue>
        {!isConnected && <S.ConnectionStatusIcon {...{ isRecentlyDisconnected, isDisconnectedForLessThanOneWeek }} />}
        {connectionStatusText}
      </Card.ItemValue>

      <Card.ItemLabel>System Status</Card.ItemLabel>
      <Card.ItemValue>
        {!modeIsIdle && <SysStatusIcon status={hehmDevice?.health?.current.hvacMode || ''} />}
        {getSystemStatus()}
      </Card.ItemValue>

    </Card>
  )
}

export default SystemConditionCard

const getDisconnectedIconColor = (isRecentlyDisconnected: boolean, isDisconnectedForLessThanOneWeek: boolean) => {
  if (isRecentlyDisconnected) return color('recentlyDisconnected')
  if (isDisconnectedForLessThanOneWeek) return color('disconnected24Hours')
  return color('disconnected7Days')
}

const S = {
  ConnectionStatusIcon: styled(LinkOffIcon).withConfig({
    shouldForwardProp: (prop) => !['isRecentlyDisconnected', 'isDisconnectedForLessThanOneWeek'].includes(prop)
  })<{ isRecentlyDisconnected: boolean; isDisconnectedForLessThanOneWeek: boolean }>`
    vertical-align: bottom;
    margin-right: 5px;
    color: ${({ isRecentlyDisconnected, isDisconnectedForLessThanOneWeek }) =>
    getDisconnectedIconColor(isRecentlyDisconnected, isDisconnectedForLessThanOneWeek)};
  `,
  FilterHealthValue: styled(Card.ItemValue)`
    display: flex;
  `,
  FilterIcon: styled.span`
    display: inline-block;
    vertical-align: bottom;
    margin-right: 5px;
    width: 25px;
    height: 25px;
    background-size: contain;
    background-repeat: no-repeat;
    background-image: url(/img/hehm_dirty_air_filter.svg);
  `,
  SystemHealthIcon: styled.span.withConfig({
    shouldForwardProp: (prop) => prop !== 'status'
  })<{ status: string }>`
    display: inline-block;
    vertical-align: bottom;
    margin-right: 5px;
    width: 25px;
    height: 25px;
    background-size: contain;
    background-repeat: no-repeat;
    background-image: url(/img/health_icon_${(props) => props.status}.svg);
  `
}
